import {getTestProps} from '@benchling/js-test-utils';
import {groupBy} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useMemo} from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, LookupStepType, SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {StaticLookupConfigSummaryChip} from 'lookup-config/LookupConfigSummaryChip';
import {
  FlowchartValidationErrorType,
  ProcessFlowchartErrorType,
  ProcessFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {runLookupConfigModalForProcessSchemaMapper} from 'processes/modals/field-mapping/SchemaFieldLookup';
import {getConditionalMappingCheckboxProps} from 'processes/settings/graph-builder-flowchart-config/conditionalMappingUtils';
import {buildDefaultSchemaFieldMapperConfigFromDataFlowEdge} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/buildSchemaMapperConfigFromStaticData';
import {getFirstFieldIdFromLookupConfig} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/buildStaticDataFromFlowchartConfigGraphQL';
import {
  doesDataFlowEdgeHandleReferenceRow,
  getFieldInfoFromDataFlowEdgeHandle,
  getSchemaFieldIdFromRowIdentifier,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {getClientId} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/getClientId';
import isLookupConfigCustomMapping from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/isLookupConfigCustomMapping';
import {
  CustomMappingData,
  FlowchartEdge,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TaskSchemaNodeSectionType,
  TransitionSource,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  TaskSchemaNode,
  TaskSchemaNodeRowData,
  isInteractiveTaskNode,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  getCustomMappingForEdge,
  getTransitionSourceForEdgeWithDefault,
  hasTransitionForEdge,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionLookupUtils';
import {useTransitionsContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsContext';
import {getExecutionNodeTypeIdentifierForNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import CollapsibleTransitionCard from './CollapsibleTransitionCard';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import {StaticTransitionSourceDescription, useTransitionCardStyles} from './TransitionCardConstants';
import {ValidationErrorRenderer} from './ValidationErrorRenderer';

export interface TransitionFieldInfoSectionProps {
  selectedFieldId: string;
  node: TaskSchemaNode;
  flowchart: {
    nodes: Array<FlowchartNode>;
    edges: Array<FlowchartEdge>;
  };
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  allSchemasById: SchemaById;
  onChangeCustomMappingData: (edgeId: string, newCustomMappingData: CustomMappingData) => void;
  onChangeSelectedFieldId: (fieldId: string | null) => void;
  locked?: boolean;
  validationErrors: Array<ProcessFlowchartValidationError>;
}

function FieldDisplay(props: {
  field: {fieldIcon: BenchlingIconName; fieldName: string | JSX.Element};
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.fieldDisplay}>
      <BenchlingIcon icon={props.field.fieldIcon} />
      {props.field.fieldName}
    </div>
  );
}

type MappingsTableProps = TransitionFieldInfoSectionProps & {
  edges: Array<FlowchartEdge>;
  showBorder?: boolean;
  className?: string;
};

type IntermediateMappingsTableProps = TransitionFieldInfoSectionProps;

function getTaskSchemaById(
  allSchemasById: SchemaById,
  schemaId: string
): ProcessTaskSchemaWithoutFlowchartConfigGraphQL | null {
  const schema = allSchemasById[schemaId] as ProcessTaskSchemaWithoutFlowchartConfigGraphQL | undefined;
  return schema ?? null;
}

function getMappingSourceSchema(
  edge: FlowchartEdge,
  nodes: Array<FlowchartNode>,
  allSchemasById: SchemaById,
  transitionSource: TransitionSource
): Schema | null {
  const sourceNode = nodes.find((node) => node.id === edge.source);
  assert(sourceNode != null, 'Expected source node to be non-null', {edge});
  assert(isInteractiveTaskNode(sourceNode), 'Expected source node to be a task node', {edge});

  const fromSchemaId = sourceNode.data.taskSchemaId;
  if (fromSchemaId == null) {
    return null;
  }
  const fromTaskSchema = getTaskSchemaById(allSchemasById, fromSchemaId);
  if (fromTaskSchema == null) {
    return null;
  }
  const {sectionType} = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const useOutputSchema =
    transitionSource === TransitionSource.OUTPUTS || sectionType === TaskSchemaNodeSectionType.OUTPUTS;
  const fromSchema = useOutputSchema ? fromTaskSchema.outputSchema : fromTaskSchema;
  if (fromSchema == null) {
    return null;
  }
  return fromSchema;
}

function EditMappingButton(props: {
  fromTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  transitionSourceSchema: Schema;
  lookupConfig: LookupConfigSpec;
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  onChangeLookupConfig: (lookupConfig: LookupConfigSpec | null) => void;
  allSchemasById: SchemaById;
  toSchemaField: SchemaField;
  fromSchemaFieldId: string;
  fromSchemaFieldSectionName: string;
  isCustomLookupConfig: boolean;
  locked?: boolean;
  isIncompatibleWithTransitionSource?: boolean;
  // Lookups require a transition between source and target to be editable
  hasTransition?: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const context = NodeDetailsSidepanelContext.useContext();
  const isDisabled =
    props.locked || props.isIncompatibleWithTransitionSource || props.hasTransition === false;

  const getAdditionalLookupConfigValidationMessage = (newLookupConfig: LookupConfigSpec): string | null => {
    let fromFieldSchema: Schema;
    switch (props.fromSchemaFieldSectionName) {
      case TaskSchemaNodeSectionType.OUTPUTS:
        if (props.fromTaskSchema.outputSchema == null) {
          return 'This node uses outputs to populate tasks but the previous node does not have an output schema.';
        }

        fromFieldSchema = props.fromTaskSchema.outputSchema;
        break;
      case TaskSchemaNodeSectionType.MAP_FROM_START:
        fromFieldSchema = props.flowchartTaskSchema;
        break;
      case TaskSchemaNodeSectionType.INPUTS:
        fromFieldSchema = props.fromTaskSchema;
        break;
      default:
        assert(false, `Expected a valid from schema field section name ${props.fromSchemaFieldSectionName}`);
    }
    const schemaFieldId = getFirstFieldIdFromLookupConfig(newLookupConfig);
    const fromSchemaField = fromFieldSchema.allFields.find(
      (field: SchemaField) => field.apiId === props.fromSchemaFieldId
    )!;
    const errorMessage = `The lookup must start with the ${fromSchemaField.name} field in the '${props.fromSchemaFieldSectionName}' section.`;
    if (schemaFieldId == null) {
      return errorMessage;
    } else if (schemaFieldId !== props.fromSchemaFieldId) {
      return errorMessage;
    }
    return null;
  };

  const tooltipText =
    props.hasTransition === false
      ? 'Custom mapping is not available because no transition exists between these task schemas. Please add a transition first.'
      : props.isIncompatibleWithTransitionSource
        ? 'Custom mapping is not available because the transition source is set to Tasks, but this mapping uses an Output field. Change the transition source to Outputs or remove this mapping.'
        : props.isCustomLookupConfig
          ? 'Edit lookup'
          : 'Configure lookup';

  return (
    <Tooltip text={tooltipText}>
      <Button
        buttonStyle={ButtonStyle.LINK}
        onClick={() => {
          runLookupConfigModalForProcessSchemaMapper({
            fromSchema: props.transitionSourceSchema,
            lookupConfig: props.lookupConfig,
            flowchartTaskSchema: props.flowchartTaskSchema,
            onSubmit: (lookupConfig) => {
              const newLookupConfig = isLookupConfigCustomMapping(lookupConfig, props.fromSchemaFieldId)
                ? lookupConfig
                : null;
              if (newLookupConfig != null) {
                analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.COMPLEX_LOOKUP_MADE, {
                  flowchartType: context.flowchartEditorConfigurator.flowchartTypeName,
                  actionMethod: ActionMethod.BUTTON_CLICK,
                  actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
                  nodeType: getExecutionNodeTypeIdentifierForNode(context.selectedNode),
                });
              }
              props.onChangeLookupConfig(newLookupConfig);
            },
            allSchemasById: props.allSchemasById,
            toSchemaField: props.toSchemaField,
            getAdditionalLookupConfigValidationMessage,
            canLookupSourceObjectBeModified: false,
          });
        }}
        disabled={isDisabled}
        icon={<BenchlingIcon icon="square-list-magnifying-glass" className={classes.actionIcon} />}
        dataTestAttrs={{
          component: 'TransitionMappingsTable',
          element: props.isCustomLookupConfig ? 'editMappingButton' : 'configureMappingButton',
        }}
      />
    </Tooltip>
  );
}

function EditMappingLookupCell(props: {
  targetField: TaskSchemaNodeRowData;
  sourceField: TaskSchemaNodeRowData;
  dataFlowEdge: FlowchartStaticEdgeData;
  nodes: Array<FlowchartNode>;
  allSchemasById: SchemaById;
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  transitionSource: TransitionSource;
  isCustomLookupConfig: boolean;
  lookupConfig: LookupConfigSpec;
  onChangeLookupConfig: (lookupConfig: LookupConfigSpec | null) => void;
  locked?: boolean;
  // Lookups require a transition between source and target to be editable
  hasTransition?: boolean;
}): JSX.Element | null {
  const {classes} = useStyles();
  assert(props.dataFlowEdge.type === ProcessFlowchartEdgeType.DATA_FLOW, 'Expected a data flow edge');
  const targetFieldId = getSchemaFieldIdFromRowIdentifier(props.targetField.clientId);

  const sourceNode = props.nodes.find((node) => node.id === props.dataFlowEdge.source);
  assert(sourceNode != null, 'Expected source node to be non-null', {edge: props.dataFlowEdge});
  assert(isInteractiveTaskNode(sourceNode), 'Expected source node to be a task node', {
    edge: props.dataFlowEdge,
  });
  const fromSchemaId = sourceNode.data.taskSchemaId;
  if (fromSchemaId == null) {
    return null;
  }

  const fromSchemaFieldId = getSchemaFieldIdFromRowIdentifier(props.sourceField.clientId);
  const {sectionType} = getFieldInfoFromDataFlowEdgeHandle(props.dataFlowEdge.sourceHandle);

  const fromTaskSchema = getTaskSchemaById(props.allSchemasById, fromSchemaId);
  if (fromTaskSchema == null) {
    return null;
  }
  const fromSchema =
    props.transitionSource === TransitionSource.OUTPUTS ? fromTaskSchema.outputSchema : fromTaskSchema;

  if (fromSchema == null) {
    return null;
  }

  const targetNode = props.nodes.find((node) => node.id === props.dataFlowEdge.target);
  assert(targetNode != null, 'Expected target node to be non-null', {edge: props.dataFlowEdge});
  assert(isInteractiveTaskNode(targetNode), 'Expected target node to be a task node', {
    edge: props.dataFlowEdge,
  });
  const toSchemaId = targetNode.data.taskSchemaId;
  if (toSchemaId == null) {
    return null;
  }
  const toTaskSchema = getTaskSchemaById(props.allSchemasById, toSchemaId);
  if (toTaskSchema == null) {
    return null;
  }
  const toSchema =
    toTaskSchema.executionType === ProcessExecutionType.FLOWCHART ? toTaskSchema.outputSchema : toTaskSchema;
  if (toSchema == null) {
    return null;
  }

  const toSchemaField = toSchema.allFields.find((field: SchemaField) => field.apiId === targetFieldId);
  assert(toSchemaField != null, 'toSchemaField should not be null');

  const lookupConfig = {...props.lookupConfig};
  // We do not support custom lookup configs for the PROCESS_TASK_DEFINITION_FIELD so when the user configures
  // one we translate to a SCHEMA_FIELD lookup step under the hood
  if (!props.isCustomLookupConfig) {
    lookupConfig.lookupSteps = [...lookupConfig.lookupSteps];
    lookupConfig.lookupSteps.pop();
    lookupConfig.lookupSteps.push({
      type: LookupStepType.SCHEMA_FIELD,
      schemaField: fromSchemaFieldId,
    });
  }
  return (
    <Cell className={classes.actionsCell}>
      <div className={classes.editMappingButtonContainer}>
        <EditMappingButton
          fromTaskSchema={fromTaskSchema}
          transitionSourceSchema={fromSchema}
          lookupConfig={lookupConfig}
          flowchartTaskSchema={props.flowchartTaskSchema}
          onChangeLookupConfig={props.onChangeLookupConfig}
          allSchemasById={props.allSchemasById}
          toSchemaField={toSchemaField}
          fromSchemaFieldId={fromSchemaFieldId}
          fromSchemaFieldSectionName={sectionType}
          isCustomLookupConfig={props.isCustomLookupConfig}
          locked={props.locked}
          isIncompatibleWithTransitionSource={
            sectionType === TaskSchemaNodeSectionType.OUTPUTS &&
            props.transitionSource === TransitionSource.INPUTS
          }
          hasTransition={props.hasTransition}
        />
        {props.isCustomLookupConfig && (
          <>
            <span>|</span>
            <Tooltip text="Reset lookup">
              <Button
                buttonStyle={ButtonStyle.LINK}
                onClick={() => {
                  props.onChangeLookupConfig(null);
                }}
                icon={<BenchlingIcon icon="trash-can" className={classes.actionIcon} />}
                dataTestAttrs={{
                  component: 'TransitionMappingsTable',
                  element: 'resetMappingButton',
                }}
                disabled={props.locked}
              />
            </Tooltip>
          </>
        )}
      </div>
    </Cell>
  );
}

function NoMappings({message}: {message: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.noMappings}>
      <span className={classes.noMappingsText}>{message}</span>
    </div>
  );
}

export function TransitionMappingsTable(props: MappingsTableProps): JSX.Element {
  const {classes} = useStyles();
  const {didInitialConfigHaveConditionalMapping} = NodeDetailsSidepanelContext.useContext();
  const getTargetField = (rowIndex: number): TaskSchemaNodeRowData | null => {
    const edge = props.edges[rowIndex];
    const targetNode = props.flowchart.nodes.find((node) => node.id === edge.target);

    if (targetNode == null || !isInteractiveTaskNode(targetNode)) {
      return null;
    }

    for (const section of targetNode.data.sections ?? []) {
      for (const row of section.sectionRows) {
        if (doesDataFlowEdgeHandleReferenceRow(edge.targetHandle, row.clientId)) {
          return row;
        }
      }
    }
    return null;
  };

  const getSourceField = (rowIndex: number): TaskSchemaNodeRowData | null => {
    const edge = props.edges[rowIndex];
    const sourceNode = props.flowchart.nodes.find((node) => node.id === edge.source);

    if (sourceNode == null || !isInteractiveTaskNode(sourceNode)) {
      return null;
    }

    for (const section of sourceNode.data.sections ?? []) {
      for (const row of section.sectionRows) {
        if (doesDataFlowEdgeHandleReferenceRow(edge.sourceHandle, row.clientId)) {
          return row;
        }
      }
    }
    return null;
  };

  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();
  const staticNodes = useMemo(() => props.flowchart.nodes.map((node) => node.data), [props.flowchart.nodes]);

  const getTransitionSourceForEdge = (dataFlowEdge: FlowchartStaticEdgeData): TransitionSource =>
    getTransitionSourceForEdgeWithDefault({
      edge: dataFlowEdge,
      nodes: staticNodes,
      transitionsById,
      ptdvPairToTransitionIdIndex,
      nodesForDefault: staticNodes,
    });

  const getCustomMappingDataForEdge = (edge: FlowchartStaticEdgeData): CustomMappingData | null =>
    getCustomMappingForEdge({
      edge,
      nodes: staticNodes,
      transitionsById,
      ptdvPairToTransitionIdIndex,
    });

  const getLookupConfigForEdge = (edge: FlowchartStaticEdgeData): LookupConfigSpec =>
    getCustomMappingDataForEdge(edge)?.lookupConfig ??
    buildDefaultSchemaFieldMapperConfigFromDataFlowEdge({
      clientId: getClientId(),
      dataFlowEdge: edge,
      nodes: props.flowchart.nodes.map((node) => node.data),
      transitions: transitionsById,
    }).lookupConfig;

  const ERROR_TYPES_TO_SHOW_IN_TABLE: Array<FlowchartValidationErrorType> = [
    ProcessFlowchartErrorType.DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH,
  ];

  const validationErrorsForTable = props.validationErrors.filter((error) =>
    props.edges.some(
      (edge) => edge.id === error.object?.clientId && ERROR_TYPES_TO_SHOW_IN_TABLE.includes(error.errorType)
    )
  );

  const hasErrors = validationErrorsForTable.length > 0;

  return (
    <>
      {hasErrors && (
        <div className={classes.errorBannerWrapper}>
          <ValidationErrorRenderer validationErrors={validationErrorsForTable} />
        </div>
      )}
      <Table
        numRows={props.edges.length}
        showBorder={props.showBorder ?? true}
        className={props.className}
        getRowClassName={(rowIndex) => {
          const edge = props.edges[rowIndex];
          const hasError = validationErrorsForTable.some((error) => error.object?.clientId === edge.id);
          return hasError ? classes.errorRow : '';
        }}
      >
        <Column
          header={<Cell className={classes.wrappingCell}>Map from</Cell>}
          renderCell={(rowIndex) => {
            const fromSchema = getMappingSourceSchema(
              props.edges[rowIndex],
              props.flowchart.nodes,
              props.allSchemasById,
              getTransitionSourceForEdge(props.edges[rowIndex])
            );
            if (fromSchema == null) {
              return <Cell />;
            }
            const sourceField = getSourceField(rowIndex);
            if (sourceField == null) {
              return <Cell />;
            }
            const edge = props.edges[rowIndex];
            const customMappingData = getCustomMappingDataForEdge(edge);
            return (
              <Cell className={classes.wrappingCell}>
                {
                  // Only show the lookup step chain if the lookup config is custom (not field to field)
                  customMappingData?.lookupConfig == null &&
                  sourceField.sectionType !== TaskSchemaNodeSectionType.MAP_FROM_START ? (
                    <FieldDisplay
                      field={{
                        fieldIcon: sourceField.fieldIcon,
                        fieldName: sourceField.fieldName,
                      }}
                    />
                  ) : (
                    <StaticLookupConfigSummaryChip
                      baseSchema={fromSchema}
                      lookupConfig={getLookupConfigForEdge(edge)}
                      context={{flowchartTaskSchema: props.flowchartTaskSchema}}
                    />
                  )
                }
              </Cell>
            );
          }}
        />
        <Column
          header={<Cell className={classes.wrappingCell}>Map to</Cell>}
          renderCell={(rowIndex) => {
            const targetField = getTargetField(rowIndex);
            if (targetField == null) {
              return <Cell />;
            }

            return (
              <Cell className={classes.wrappingCell}>
                <FieldDisplay
                  field={{
                    fieldIcon: targetField.fieldIcon,
                    fieldName: targetField.fieldName,
                  }}
                />
              </Cell>
            );
          }}
        />
        {didInitialConfigHaveConditionalMapping && (
          <Column
            header={
              <Cell className={classes.wrappingCell}>
                <span>
                  <span>Required</span>
                  <Tooltip text="Check the box to prevent items with empty or null values for the specified field from being created.">
                    <BenchlingIcon icon="info-circle" className={classes.helpIcon} />
                  </Tooltip>
                </span>
              </Cell>
            }
            renderCell={(rowIndex) => {
              const inputId = _.uniqueId('resultMustBeNonNullToMap');
              const edge = props.edges[rowIndex];
              const customMappingData = getCustomMappingDataForEdge(edge);
              const isChecked = customMappingData?.resultMustBeNonNullToMap ?? false;
              return (
                <Cell>
                  <CheckboxInput
                    id={inputId}
                    checked={isChecked}
                    label="Require field to map"
                    labelStyle={LabelStyle.HIDDEN}
                    onChange={(e) => {
                      props.onChangeCustomMappingData(edge.id, {
                        ...customMappingData,
                        resultMustBeNonNullToMap: e.target.checked,
                      });
                    }}
                    {...getConditionalMappingCheckboxProps({
                      isLocked: props.locked,
                      isChecked,
                    })}
                  />
                </Cell>
              );
            }}
          />
        )}
        <Column
          header={<Cell className={classes.actionsCell} />}
          renderCell={(rowIndex) => {
            const targetField = getTargetField(rowIndex);
            const sourceField = getSourceField(rowIndex);
            if (targetField == null || sourceField == null) {
              return null;
            }
            const edge = props.edges[rowIndex];
            const customMappingData = getCustomMappingDataForEdge(edge);
            return (
              <EditMappingLookupCell
                targetField={targetField}
                sourceField={sourceField}
                dataFlowEdge={edge}
                nodes={props.flowchart.nodes}
                allSchemasById={props.allSchemasById}
                flowchartTaskSchema={props.flowchartTaskSchema}
                transitionSource={getTransitionSourceForEdge(edge)}
                lookupConfig={getLookupConfigForEdge(edge)}
                onChangeLookupConfig={(lookupConfig) => {
                  props.onChangeCustomMappingData(edge.id, {
                    ...customMappingData,
                    lookupConfig: lookupConfig ?? undefined,
                  });
                }}
                isCustomLookupConfig={customMappingData?.lookupConfig != null}
                locked={props.locked}
                hasTransition={hasTransitionForEdge({edge, nodes: staticNodes, ptdvPairToTransitionIdIndex})}
              />
            );
          }}
        />
      </Table>
    </>
  );
}

function TransitionMappingsTableForMultipleConnections(
  props: MappingsTableProps & {
    getEdgeGroupKey: (edge: FlowchartEdge) => string;
    connectedNodeLabel: string;
    emptyMessage: string;
    description: string;
  }
): JSX.Element {
  const {classes} = useStyles();
  const {classes: cardClasses} = useTransitionCardStyles();
  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();
  const staticNodes = useMemo(() => props.flowchart.nodes.map((node) => node.data), [props.flowchart.nodes]);
  const edgesByNodeId = groupBy(props.edges, props.getEdgeGroupKey);
  const sortedNodes = props.flowchart.nodes
    .filter((node) => edgesByNodeId[node.id] != null)
    .sort((leftNode, rightNode) => leftNode.data.nodeName.localeCompare(rightNode.data.nodeName));

  if (props.edges.length === 0) {
    return <NoMappings message={props.emptyMessage} />;
  }

  return (
    <>
      <div className={classes.mappingSectionDescription}>{props.description}</div>
      {sortedNodes.map((node) => {
        const edges = edgesByNodeId[node.id];
        // Edges from same node should have the same transition source
        const firstEdge = edges[0] as FlowchartStaticEdgeData;
        const transitionSource = getTransitionSourceForEdgeWithDefault({
          edge: firstEdge,
          nodes: staticNodes,
          transitionsById,
          ptdvPairToTransitionIdIndex,
          nodesForDefault: staticNodes,
        });
        return (
          <CollapsibleTransitionCard
            key={node.id}
            header={props.connectedNodeLabel}
            headerAdornment={
              <Chip
                className={cardClasses.chipNormalWeight}
                size={ChipSize.MINI}
                icon={ModelTypeConstants.PROCESS_TASK}
                text={node.data.nodeName}
              />
            }
            alwaysVisibleContent={
              <StaticTransitionSourceDescription
                transitionSource={transitionSource}
                className={classes.transitionSourceDescription}
              />
            }
            isOpenByDefault={true}
            fullWidthChildren={true}
            dataTestAttrs={{
              component: 'TransitionFieldInfoSection',
              element: 'mappingsTable',
            }}
          >
            <TransitionMappingsTable
              {...props}
              edges={edges}
              showBorder={false}
              className={cardClasses.flushTable}
            />
          </CollapsibleTransitionCard>
        );
      })}
    </>
  );
}

function InboundMappingsTable(props: IntermediateMappingsTableProps): JSX.Element {
  const inboundEdges = props.flowchart.edges.filter((edge) => {
    if (
      edge.type !== ProcessFlowchartEdgeType.DATA_FLOW ||
      edge.target !== props.node.id ||
      edge.targetHandle == null
    ) {
      return false;
    }
    const targetFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
    return targetFieldClientInfo.fieldClientId === getSchemaFieldIdFromRowIdentifier(props.selectedFieldId);
  });

  return (
    <TransitionMappingsTableForMultipleConnections
      {...props}
      edges={inboundEdges}
      getEdgeGroupKey={(edge) => edge.source}
      connectedNodeLabel="From"
      emptyMessage="Draw mappings to this field to view & edit them here."
      description="View & edit how data is mapped to this field."
    />
  );
}

function OutboundMappingsTable(props: IntermediateMappingsTableProps): JSX.Element {
  const outboundEdges = props.flowchart.edges.filter((edge) => {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW || edge.sourceHandle == null) {
      return false;
    }
    const sourceFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);

    if (
      props.node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS &&
      sourceFieldClientInfo.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
    ) {
      return sourceFieldClientInfo.fieldClientId === getSchemaFieldIdFromRowIdentifier(props.selectedFieldId);
    } else if (edge.source !== props.node.id) {
      return false;
    }

    return sourceFieldClientInfo.fieldClientId === getSchemaFieldIdFromRowIdentifier(props.selectedFieldId);
  });

  return (
    <TransitionMappingsTableForMultipleConnections
      {...props}
      edges={outboundEdges}
      getEdgeGroupKey={(edge) => edge.target}
      connectedNodeLabel="To"
      emptyMessage="Draw mappings from this field to view & edit them here."
      description="View & edit how data is mapped from this field."
    />
  );
}

function FieldDescription(props: {
  selectedField: TaskSchemaNodeRowData;
  nodeName: string;
  onClickTask: () => void;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <>
      <div className={classes.sectionHeading}>Field details</div>
      <div className={classes.fieldMetadataRow}>
        <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'name'})}>
          <div className={classes.fieldMetadataLabel}>Name</div>
          <div className={classes.wrappingText}>{props.selectedField.fieldName}</div>
        </div>
        <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'definition'})}>
          <div className={classes.fieldMetadataLabel}>Definition</div>
          <FieldDisplay
            field={{
              fieldIcon: props.selectedField.fieldIcon,
              fieldName: props.selectedField.fieldTypeLabel,
            }}
          />
        </div>
        <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'task'})}>
          <div className={classes.fieldMetadataLabel}>Task</div>
          <Button buttonStyle={ButtonStyle.LINK} onClick={props.onClickTask} text={props.nodeName} />
        </div>
      </div>
    </>
  );
}

export default function TransitionFieldInfoSection(props: TransitionFieldInfoSectionProps): JSX.Element {
  const {classes} = useStyles();
  const selectedField = useMemo(() => {
    assert(isInteractiveTaskNode(props.node), 'Expected the selected node to be an interactive task node', {
      node: props.node,
    });

    for (const section of props.node.data.sections) {
      for (const row of section.sectionRows) {
        if (row.clientId === props.selectedFieldId) {
          return row;
        }
      }
    }

    assert(
      false,
      `Expected to find the selected field ${props.selectedFieldId} in the selected node ${props.node.data.nodeName}`,
      {
        selectedFieldId: props.selectedFieldId,
        nodeId: props.node,
      }
    );
  }, [props.selectedFieldId, props.node]);

  // The selected node is the source if there is a control flow edge from the selected node to another node.
  // Otherwise the node is a "target" node.
  const nodeIsTarget = props.flowchart.edges.some(
    (edge) =>
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
      edge.target === props.node.id &&
      edge.targetHandle != null
  );

  return (
    <div className={classes.container}>
      <FieldDescription
        selectedField={selectedField}
        nodeName={props.node.data.nodeName}
        onClickTask={() => {
          props.onChangeSelectedFieldId(null);
        }}
      />

      {nodeIsTarget && (
        <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'inbound-mappings'})}>
          <div className={classes.mappingSectionTitle}>Inbound mappings</div>
          <InboundMappingsTable {...props} />
        </div>
      )}
      <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'outbound-mappings'})}>
        <div className={classes.mappingSectionTitle}>Outbound mappings</div>
        <OutboundMappingsTable {...props} />
      </div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.lg,
  },
  sectionHeading: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
    color: theme.colors.gray[9],
  },
  fieldMetadataRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 8,
  },
  mappingSectionTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
    color: theme.colors.gray[9],
    marginBottom: 4,
  },
  mappingSectionDescription: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[9],
    marginBottom: theme.spacing.sm,
  },
  fieldDisplay: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
  },
  helpIcon: {
    marginLeft: 8,
  },
  editMappingButtonContainer: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
  },
  actionIcon: {
    color: theme.colors.gray[4],
    '&:hover': {
      color: theme.colors.blue[6],
    },
  },
  noMappings: {
    paddingTop: 4,
  },
  noMappingsText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
  },
  wrappingCell: {
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    width: 'auto',
    textWrap: 'pretty',
  },
  actionsCell: {
    width: 50,
  },
  errorRow: {
    backgroundColor: theme.colors.red[0],
    borderLeft: `4px solid ${theme.colors.red[8]}`,
  },
  wrappingText: {
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  },
  fieldMetadataLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    color: theme.colors.gray[9],
    marginBottom: 2,
  },
  transitionSourceDescription: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[7],
    paddingBottom: theme.spacing.sm,
  },
  errorBannerWrapper: {
    marginTop: -4,
    '& > div': {
      marginBottom: 0,
    },
  },
}));
